import type { KeyboardEvent, RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

export type UseTableKeyboardNavigationArgs<T> = {
  /** Rows in the exact order the table currently renders them. Pre-sort this
   * yourself if a column carries `defaultSortOrder` — the hook does not
   * re-derive antd's sort. */
  rows: T[]
  /** Changing this value clears the highlight and returns to page 1. Pass the
   * search term (and any other input that reorders/refilters `rows`). */
  resetKey: unknown
  /** Called when Enter is pressed on the highlighted row. */
  onActivate: (row: T) => void
  /** Initial page size for antd's controlled pagination. Omit for a table
   * rendered with `pagination={false}` — arrow keys then clamp against
   * `rows.length` with no page-flip math, and `currentPage`/`pageSize`/
   * `onPageChange` in the result can be ignored. */
  pageSize?: number
  /** A class name this hook's `getRowClassName` should always emit alongside
   * `is-keyboard-focused` (e.g. a table's existing `'is-clickable-row'`). */
  extraRowClassName?: string
  /** Rows the highlight/Enter should treat as inert (e.g. an optimistic
   * create row with no id yet). */
  isRowDisabled?: (row: T) => boolean
  /** `false` makes every key a no-op. Defaults to `true`. */
  enabled?: boolean
}

export type UseTableKeyboardNavigationResult<T> = {
  highlightedIndex: number | null
  currentPage: number
  pageSize: number
  /** Attach to the table's outer wrapper so the highlighted row can be
   * located via `.is-keyboard-focused` and scrolled into view. */
  containerRef: RefObject<HTMLDivElement>
  onSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
  /** Wire to antd's `pagination.onChange`. */
  onPageChange: (page: number, size: number) => void
  /** Wire to antd's `Table.onChange`, passing `extra.currentDataSource` —
   * keeps the highlighted index pointing at the right row after a
   * user-driven re-sort or column filter. */
  onTableChange: (tableRows: T[]) => void
  /** Wire to antd's `Table.rowClassName`. */
  getRowClassName: (row: T, index: number) => string
}

/**
 * Arrow-key row navigation for an antd `Table` driven from a search input,
 * matching the "Your Hubs" hub selector on the home page
 * (`features/projects`) — the only place in the app where the shared
 * `is-keyboard-focused` highlight class is actually applied. This hook
 * generalizes that implementation (`features/projects/Container.tsx` +
 * `ProjectList.tsx`) so a fourth-through-Nth table doesn't hand-roll it
 * again, and adds scroll-into-view, which the reference lacks.
 *
 * `highlightedIndex` is an absolute index across all pages. ArrowDown/Up
 * clamp at the ends (no wrap); the first ArrowUp from `null` lands on index
 * 0; Enter activates without `preventDefault()`; Escape is left unhandled,
 * same as the reference.
 */
// highlightedIndex and currentPage change together off one arrow-key press
// (crossing a page boundary moves both), so they live in one state object
// updated by one setState call. Splitting them into two useState calls and
// deriving the page from inside setHighlightedIndex's updater — the
// reference implementation's (features/projects/Container.tsx) shape — is
// an updater with a side effect (a second, different setState), which
// double-fires under StrictMode's double-invoked updaters. A single
// combined updater has no such side effect: it only reads closed-over
// values and returns a new object, so double-invoking it is inert, and
// because it is one queued update, two ArrowDowns dispatched in the same
// batch (key repeat, or two `act()`-wrapped calls in a test) still chain
// correctly through React's per-state pending-update queue.
type NavState = { highlightedIndex: number | null; currentPage: number }

export const useTableKeyboardNavigation = <T>({
  rows,
  resetKey,
  onActivate,
  pageSize: initialPageSize,
  extraRowClassName,
  isRowDisabled,
  enabled = true,
}: UseTableKeyboardNavigationArgs<T>): UseTableKeyboardNavigationResult<T> => {
  const isPaginated = initialPageSize != null

  const [nav, setNav] = useState<NavState>({
    highlightedIndex: null,
    currentPage: 1,
  })
  const { highlightedIndex, currentPage } = nav
  const [pageSize, setPageSize] = useState(initialPageSize ?? 0)

  // Mirrors the table's actual visible order. Seeded from `rows` (the
  // caller is expected to hand `dataSource` the same, already-sorted array);
  // re-seeded whenever `rows` changes; overwritten by `onTableChange` when
  // the user re-sorts or filters via the column UI.
  const [visibleRows, setVisibleRows] = useState<T[]>(rows)
  useEffect(() => {
    setVisibleRows(rows)
  }, [rows])

  useEffect(() => {
    setNav({ highlightedIndex: null, currentPage: 1 })
  }, [resetKey])

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    containerRef.current
      ?.querySelector('.is-keyboard-focused')
      ?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, currentPage])

  const onTableChange = useCallback((tableRows: T[]) => {
    setVisibleRows(tableRows)
  }, [])

  const onPageChange = useCallback((page: number, size: number) => {
    setNav((prev) => ({ ...prev, currentPage: page }))
    setPageSize(size)
  }, [])

  const onSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!enabled) return

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        const direction = event.key === 'ArrowDown' ? 1 : -1
        setNav((prev) => {
          const next =
            prev.highlightedIndex == null
              ? 0
              : Math.min(
                  Math.max(prev.highlightedIndex + direction, 0),
                  visibleRows.length - 1,
                )
          const targetPage = isPaginated
            ? Math.floor(next / pageSize) + 1
            : prev.currentPage
          return { highlightedIndex: next, currentPage: targetPage }
        })
      } else if (event.key === 'Enter') {
        if (highlightedIndex == null) return
        const row = visibleRows[highlightedIndex]
        if (!row || isRowDisabled?.(row)) return
        onActivate(row)
      }
    },
    [
      enabled,
      highlightedIndex,
      visibleRows,
      isPaginated,
      pageSize,
      isRowDisabled,
      onActivate,
    ],
  )

  const getRowClassName = useCallback(
    (_: T, index: number): string => {
      const absoluteIndex = isPaginated
        ? (currentPage - 1) * pageSize + index
        : index
      return [
        extraRowClassName,
        absoluteIndex === highlightedIndex ? 'is-keyboard-focused' : null,
      ]
        .filter(Boolean)
        .join(' ')
    },
    [isPaginated, currentPage, pageSize, highlightedIndex, extraRowClassName],
  )

  return {
    highlightedIndex,
    currentPage,
    pageSize,
    containerRef,
    onSearchKeyDown,
    onPageChange,
    onTableChange,
    getRowClassName,
  }
}
